// Formulario de alta manual de un Delivery Current -- compartido entre
// Admin (DeliveriesLoad.tsx) y Seller (SellerDeliveryCreate.tsx). Ver
// "LYNX — unificación External API/Admin/Seller": un solo formulario, un
// solo POST /v2/deliveries atómico (Store+Account+precio+lynx_code),
// nunca dos implementaciones completas de la misma carga.
//
// Quién puede elegir qué Store es decisión del padre (Admin: todas las
// Stores; Seller: solo las suyas, resueltas vía sellerOwnsStore en el
// backend) -- este componente solo sabe renderizar la lista que le pasan
// y, si `lockedStoreId` viene seteado, ocultar el selector porque ya está
// decidido (Seller con una sola Store autorizada).
import React, { useEffect, useMemo, useState } from "react";
import Input from "../ui/Input";
import Button from "../ui/Button";
import { authFetch } from "../../lib/authFetch";

export type DeliveryIntakeStore = {
  id: number | string;
  nombre_fantasia?: string;
  nombreFantasia?: string;
  categoria?: string;
};

type AccountItem = {
  id: number;
  provider?: string;
  display_name?: string;
  displayName?: string;
};

export type DeliveryCreatedResult = {
  deliveryID: number;
  storeId: number;
  accountId: number;
  storeName: string;
  accountName: string;
  nombreDestinatario: string;
  lynxCode: string;
  cantidadBultos: number;
};

type FormState = {
  storeId: string;
  accountId: string;
  nombreDestinatario: string;
  telefonoDestinatario: string;
  direccion: string;
  codigoPostal: string;
  localidad: string;
  provincia: string;
  tipoEnvio: string;
  comentarioDestino: string;
  aditionalNotes: string;
  cantidadBultos: string;
  importeCobranza: string;
  externalReference: string;
  isTurbo: boolean;
};

const emptyForm = (storeId = ""): FormState => ({
  storeId,
  accountId: "",
  nombreDestinatario: "",
  telefonoDestinatario: "",
  direccion: "",
  codigoPostal: "",
  localidad: "",
  provincia: "",
  tipoEnvio: "flex",
  comentarioDestino: "",
  aditionalNotes: "",
  cantidadBultos: "1",
  importeCobranza: "",
  externalReference: "",
  isTurbo: false,
});

const gridStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" };
const singleColumnStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr", gap: "16px" };
const twoGridStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "14px" };
const selectWrapperStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: "8px" };
const selectLabelStyle: React.CSSProperties = { fontSize: "14px", fontWeight: 600, color: "var(--text-primary)" };
const selectStyle: React.CSSProperties = {
  width: "100%", minHeight: "48px", padding: "0 16px", borderRadius: "16px",
  border: "1px solid var(--border)", outline: "none", background: "var(--surface-alt)",
  color: "var(--text-primary)", fontSize: "15px",
};
const textareaStyle: React.CSSProperties = {
  width: "100%", minHeight: "72px", padding: "12px 16px", borderRadius: "16px",
  border: "1px solid var(--border)", outline: "none", background: "var(--surface-alt)",
  color: "var(--text-primary)", fontSize: "15px", fontFamily: "inherit", resize: "vertical",
};
const switchRowStyle: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px",
  minHeight: "54px", padding: "0 16px", borderRadius: "16px", border: "1px solid var(--border)",
  background: "var(--surface-alt)",
};
const helperStyle: React.CSSProperties = { fontSize: "13px", color: "var(--text-secondary)" };
const actionsRowStyle: React.CSSProperties = { display: "flex", gap: "12px", flexWrap: "wrap" };
const alertStyle = (tone: "info" | "danger"): React.CSSProperties => ({
  borderRadius: "16px", padding: "14px 16px", fontSize: "14px",
  background: tone === "danger" ? "rgba(185,100,95,0.14)" : "rgba(198,165,106,0.08)",
  border: tone === "danger" ? "1px solid rgba(185,100,95,0.24)" : "1px solid var(--border)",
  color: tone === "danger" ? "var(--danger)" : "var(--text-primary)",
});

function storeName(x: DeliveryIntakeStore) {
  return x.nombre_fantasia || x.nombreFantasia || `Store ${x.id}`;
}

function accountLabel(x: AccountItem) {
  const name = x.display_name || x.displayName || `Account ${x.id}`;
  const provider = x.provider || "—";
  return `${name} · ${provider}`;
}

function SelectField({
  label, value, onChange, options, disabled, placeholder = "Seleccionar",
}: {
  label: string; value: string; onChange: (value: string) => void;
  options: { value: string; label: string }[]; disabled?: boolean; placeholder?: string;
}) {
  return (
    <label style={selectWrapperStyle}>
      <span style={selectLabelStyle}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{ ...selectStyle, opacity: disabled ? 0.65 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}

export default function DeliveryIntakeForm({
  token,
  stores,
  storesLoading = false,
  lockedStoreId,
  onCreated,
}: {
  token: string | null;
  stores: DeliveryIntakeStore[];
  storesLoading?: boolean;
  lockedStoreId?: string;
  onCreated: (result: DeliveryCreatedResult) => void;
}) {
  const [form, setForm] = useState<FormState>(() => emptyForm(lockedStoreId || ""));
  const [storeScopedAccounts, setStoreScopedAccounts] = useState<AccountItem[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountsHelper, setAccountsHelper] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Si el padre decide (o cambia) la Store bloqueada -- Seller con 1 sola
  // Store autorizada -- se refleja acá sin que el usuario pueda tocarla.
  useEffect(() => {
    if (lockedStoreId) setForm((prev) => ({ ...prev, storeId: lockedStoreId }));
  }, [lockedStoreId]);

  useEffect(() => {
    const loadAccounts = async () => {
      if (!form.storeId) {
        setStoreScopedAccounts([]);
        setAccountsHelper("");
        return;
      }
      setLoadingAccounts(true);
      setAccountsHelper("");
      setStoreScopedAccounts([]);
      setForm((prev) => ({ ...prev, accountId: "" }));
      try {
        const res = await authFetch(`/v2/stores/${form.storeId}/account`, { token });
        const json = await res.json().catch(() => ({} as any));
        if (!res.ok || json?.ok === false) throw new Error(json?.error || "No se pudieron leer las accounts del store");
        const list: AccountItem[] = Array.isArray(json.accounts) ? json.accounts : [];
        setStoreScopedAccounts(list);
        if (list.length === 1) {
          setForm((prev) => ({ ...prev, accountId: String(list[0].id) }));
          setAccountsHelper("El store tiene una sola account asociada. Se seleccionó automáticamente.");
        } else if (list.length > 1) {
          setAccountsHelper("El store tiene más de una account asociada. Seleccioná el canal correspondiente.");
        } else {
          setAccountsHelper("Esta tienda todavía no tiene ninguna account vinculada.");
        }
      } catch {
        setStoreScopedAccounts([]);
        setAccountsHelper("No se pudieron leer las accounts asociadas a este store.");
      } finally {
        setLoadingAccounts(false);
      }
    };
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.storeId, token]);

  const storeOptions = useMemo(
    () => stores.filter((s) => s.id != null).map((s) => ({ value: String(s.id), label: `${storeName(s)}${s.categoria ? " · " + s.categoria : ""}` })),
    [stores]
  );
  const accountOptions = storeScopedAccounts
    .filter((a) => a.id != null)
    .map((a) => ({ value: String(a.id), label: accountLabel(a) }));

  const selectedStore = stores.find((s) => String(s.id) === form.storeId) || null;
  const selectedAccount = storeScopedAccounts.find((a) => String(a.id) === form.accountId) || null;

  const handleChange = (field: keyof FormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const cantidadBultosNum = Number(form.cantidadBultos || "1");
  const cantidadBultosValida = Number.isInteger(cantidadBultosNum) && cantidadBultosNum > 0;
  const importeCobranzaValido = form.importeCobranza.trim() === "" || (Number.isFinite(Number(form.importeCobranza)) && Number(form.importeCobranza) >= 0);

  const canSubmit =
    Boolean(form.storeId) &&
    Boolean(form.accountId) &&
    Boolean(form.nombreDestinatario.trim()) &&
    Boolean(form.direccion.trim()) &&
    Boolean(form.codigoPostal.trim()) &&
    Boolean(form.localidad.trim()) &&
    Boolean(form.provincia.trim()) &&
    Boolean(form.tipoEnvio.trim()) &&
    cantidadBultosValida &&
    importeCobranzaValido;

  const resetKeepingStore = () => {
    setForm((prev) => emptyForm(lockedStoreId || prev.storeId));
    setError("");
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");

    const payload = {
      store_id: Number(form.storeId),
      account_id: Number(form.accountId),
      nombre_destinatario: form.nombreDestinatario.trim(),
      telefono_destinatario: form.telefonoDestinatario.trim() || undefined,
      direccion: form.direccion.trim(),
      codigo_postal: form.codigoPostal.trim(),
      localidad: form.localidad.trim(),
      provincia: form.provincia.trim(),
      tipo_envio: form.tipoEnvio.trim(),
      comentario_destino: form.comentarioDestino.trim() || undefined,
      aditional_notes: form.aditionalNotes.trim() || undefined,
      cantidad_bultos: cantidadBultosNum,
      importe_cobranza: form.importeCobranza.trim() === "" ? undefined : Number(form.importeCobranza),
      external_reference: form.externalReference.trim() || undefined,
      is_turbo: form.isTurbo,
    };

    try {
      setSubmitting(true);
      const res = await authFetch("/v2/deliveries", { method: "POST", token, json: true, body: JSON.stringify(payload) });
      const json = await res.json().catch(() => ({} as any));
      if (!res.ok || json?.ok === false) throw new Error(json?.error || "No se pudo crear el envío");

      onCreated({
        deliveryID: json.deliveryID,
        storeId: json.store_id,
        accountId: json.account_id,
        storeName: selectedStore ? storeName(selectedStore) : `Store ${json.store_id}`,
        accountName: selectedAccount ? accountLabel(selectedAccount) : `Account ${json.account_id}`,
        nombreDestinatario: form.nombreDestinatario.trim(),
        lynxCode: json.lynx_code,
        cantidadBultos: json.cantidad_bultos ?? cantidadBultosNum,
      });

      resetKeepingStore();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al crear el envío");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {error ? <div style={alertStyle("danger")}>{error}</div> : null}

      {!lockedStoreId ? (
        <div style={twoGridStyle}>
          <SelectField
            label="Store"
            value={form.storeId}
            onChange={(v) => handleChange("storeId", v)}
            options={storeOptions}
            disabled={storesLoading}
            placeholder={storesLoading ? "Cargando stores..." : "Seleccionar store"}
          />
          {form.storeId ? (
            <SelectField
              label="Account"
              value={form.accountId}
              onChange={(v) => handleChange("accountId", v)}
              options={accountOptions}
              disabled={loadingAccounts || storeScopedAccounts.length === 1}
              placeholder={loadingAccounts ? "Cargando accounts..." : storeScopedAccounts.length === 1 ? "Account seleccionada automáticamente" : "Seleccionar account"}
            />
          ) : null}
        </div>
      ) : (
        <SelectField
          label="Account"
          value={form.accountId}
          onChange={(v) => handleChange("accountId", v)}
          options={accountOptions}
          disabled={loadingAccounts || storeScopedAccounts.length === 1}
          placeholder={loadingAccounts ? "Cargando accounts..." : storeScopedAccounts.length === 1 ? "Account seleccionada automáticamente" : "Seleccionar account"}
        />
      )}
      {accountsHelper ? <div style={alertStyle("info")}>{accountsHelper}</div> : null}

      <div style={gridStyle}>
        <Input label="Nombre destinatario" placeholder="Ej. Juan Pérez" value={form.nombreDestinatario} onChange={(e) => handleChange("nombreDestinatario", e.target.value)} />
        <Input label="Teléfono destinatario" placeholder="Ej. 11 5555-1234" value={form.telefonoDestinatario} onChange={(e) => handleChange("telefonoDestinatario", e.target.value)} />
      </div>

      <Input label="Dirección" placeholder="Ej. Av Rivadavia 1234" value={form.direccion} onChange={(e) => handleChange("direccion", e.target.value)} />

      <div style={gridStyle}>
        <Input label="Código postal" placeholder="Ej. 1406" value={form.codigoPostal} onChange={(e) => handleChange("codigoPostal", e.target.value)} />
        <Input label="Localidad" placeholder="Ej. CABA / Morón / Rosario" value={form.localidad} onChange={(e) => handleChange("localidad", e.target.value)} />
      </div>

      <div style={gridStyle}>
        <Input label="Provincia" placeholder="Ej. Buenos Aires" value={form.provincia} onChange={(e) => handleChange("provincia", e.target.value)} />
        <SelectField
          label="Tipo de envío"
          value={form.tipoEnvio}
          onChange={(v) => handleChange("tipoEnvio", v)}
          options={[{ value: "flex", label: "Flex" }, { value: "manual", label: "Manual" }, { value: "turbo", label: "Turbo" }]}
        />
      </div>

      <div style={singleColumnStyle}>
        <label style={selectWrapperStyle}>
          <span style={selectLabelStyle}>Observación / comentario destino</span>
          <textarea style={textareaStyle} value={form.comentarioDestino} onChange={(e) => handleChange("comentarioDestino", e.target.value)} placeholder="Instrucciones para el chofer o el destinatario" />
        </label>
        <label style={selectWrapperStyle}>
          <span style={selectLabelStyle}>Notas adicionales</span>
          <textarea style={textareaStyle} value={form.aditionalNotes} onChange={(e) => handleChange("aditionalNotes", e.target.value)} placeholder="Notas internas (opcional)" />
        </label>
      </div>

      <div style={gridStyle}>
        <Input
          label="Cantidad de bultos"
          type="number"
          min={1}
          value={form.cantidadBultos}
          onChange={(e) => handleChange("cantidadBultos", e.target.value)}
          error={!cantidadBultosValida ? "Debe ser un entero mayor a 0" : undefined}
        />
        <Input
          label="Importe a cobrar (opcional)"
          type="number"
          min={0}
          placeholder="Dejar vacío si no hay cobranza"
          value={form.importeCobranza}
          onChange={(e) => handleChange("importeCobranza", e.target.value)}
          error={!importeCobranzaValido ? "Debe ser un número mayor o igual a 0" : undefined}
        />
      </div>

      <div style={gridStyle}>
        <Input label="Referencia externa (opcional)" placeholder="Ej. número de pedido propio" value={form.externalReference} onChange={(e) => handleChange("externalReference", e.target.value)} />
        <div style={selectWrapperStyle}>
          <span style={selectLabelStyle}>Turbo</span>
          <div style={switchRowStyle}>
            <div>
              <div style={{ fontWeight: 700, color: "var(--text-primary)" }}>Envío turbo</div>
              <div style={helperStyle}>Activa <code>is_turbo</code> en el envío.</div>
            </div>
            <input type="checkbox" checked={form.isTurbo} onChange={(e) => handleChange("isTurbo", e.target.checked)} style={{ width: 18, height: 18 }} />
          </div>
        </div>
      </div>

      <div style={actionsRowStyle}>
        <Button type="submit" variant="primary" disabled={!canSubmit || submitting}>
          {submitting ? "Creando..." : "Crear envío"}
        </Button>
        <Button type="button" variant="secondary" onClick={resetKeepingStore} disabled={submitting}>
          Limpiar
        </Button>
      </div>
    </form>
  );
}
